import React, { useEffect, useRef } from 'react';
import * as R from 'ramda';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

// Utils
import { bool, func, number, shape, string } from 'prop-types';
import { useIntersect } from 'hooks';
import { CardBody, CardFooter, CardHeader } from './card-components';

const AuthSequencingCard = ({ card, cardHeight, index, setCardHeight }) => {
  const {
    loadingAllowed,
    locationId,
    productId,
    nameShort,
    proprietary,
    sequencedCount,
    notSequencedCount,
  } = card;
  // any function ran here will be evaluated on every rerender, regardless if hideCard is true or false
  // please put any specific logic for components inside the component to ensure it is only ran when hideCard is false
  // this is especially important when 188+ cards are rendered, as you are evaluating a signficant amount of logic
  const { entry, setNode } = useIntersect({ rootMargin: '300px' });
  const node = useRef();

  useEffect(() => {
    setNode(node.current);
  }, [setNode]);

  // since the first 10 cards are always rendered, we can pull the card height off the first one right away
  // allows us to make future changes to the cards without worrying about keeping the card heights
  useEffect(() => {
    if (index === 0) setCardHeight(node.current.getBoundingClientRect().height);
  }, [index, setCardHeight]);

  // isIntersecting fixes a mobile bug that causes cards just outside the parent to not render
  const { intersectionRatio, isIntersecting } = entry;
  // show the first 10 cards on initial render, when intersectionRatio is undefined
  // otherwise, only show if intersectionRatio > 0
  // keeps from showing blank cards on initial load
  const hideCard =
    (R.isNil(intersectionRatio) && index > 10) || (intersectionRatio === 0 && !isIntersecting);

  return (
    <Card
      ref={node}
      sx={{
        height: !hideCard && cardHeight ? cardHeight : 'auto',
        minHeight: 234,
      }}
      variant="loading"
    >
      <CardContent variant="loading">
        {!hideCard && (
          <>
            <CardHeader proprietary={proprietary} title={nameShort} />
            <CardBody
              loadingAllowed={loadingAllowed}
              notSequencedCount={notSequencedCount}
              sequencedCount={sequencedCount}
            />
            <CardFooter locationId={locationId} productId={productId} />
          </>
        )}
      </CardContent>
    </Card>
  );
};

AuthSequencingCard.propTypes = {
  card: shape({
    loadingAllowed: bool.isRequired,
    locationId: number.isRequired,
    nameShort: string.isRequired,
    notSequencedCount: number,
    productId: number.isRequired,
    proprietary: bool,
    sequencedCount: number,
  }).isRequired,
  cardHeight: number.isRequired,
  index: number.isRequired,
  setCardHeight: func.isRequired,
};

export default AuthSequencingCard;
